import React, { useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { MyRichTextEditor } from "../../../components/custom/my-rich-text-editor";
import {
  messageTemplateCreateSchema,
  messageTemplateUpdateSchema,
  type MessageTemplate,
  type MessageTemplateCategory,
  type MessageTemplateCreateFormValues,
  type MessageTemplateUpdateFormValues,
} from "@/types/message-template-type";
import {
  createMessageTemplate,
  updateMessageTemplate,
} from "@/api/message-template-api";
import { useToast } from "@/hooks/use-toast";
import { SaveButton } from "@/components/ui/buttons/save-button";
import { MessageTemplateUpdateRequest } from "@/types/message-template-type";
import { FormHandlers, useExposeFormHandlers } from "@/hooks/use-form-handlers";

export type MessageTemplateFormHandlers = FormHandlers;

type FormMode = "create" | "update";

interface MessageTemplateFormProps {
  mode: FormMode;
  initialValues?: Partial<MessageTemplate> & { id?: string };
  onSuccess?: (saved: MessageTemplate) => void;
  onCancel?: () => void;
  onFormReady?: (handlers: FormHandlers) => void;
}

const categoryOptions: MessageTemplateCategory[] = [
  "General",
  "Welcome",
  "Order",
  "Shipping",
  "FollowUp",
];

export function MessageTemplateForm({
  mode,
  initialValues,
  onSuccess,
  onCancel,
  onFormReady,
}: MessageTemplateFormProps) {
  const { toast } = useToast();

  const isCreate = mode === "create";

  const resolver = zodResolver(
    isCreate ? messageTemplateCreateSchema : messageTemplateUpdateSchema
  );

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isSubmitting, isValid },
  } = useForm<
    MessageTemplateCreateFormValues | MessageTemplateUpdateFormValues
  >({
    resolver,
    mode: "onChange",
    reValidateMode: "onBlur",
    defaultValues: {
      key: initialValues?.key ?? "",
      name: initialValues?.name ?? "",
      category: initialValues?.category ?? "General",
      content: initialValues?.content ?? "",
      isActive: initialValues?.isActive ?? true,
    },
  });

  const onSubmit: SubmitHandler<
    MessageTemplateCreateFormValues | MessageTemplateUpdateFormValues
  > = async (values) => {
    try {
      let saved: any;
      if (isCreate) {
        saved = await createMessageTemplate(
          values as MessageTemplateCreateFormValues
        );
      } else {
        const id = (initialValues as any)?.id as string | undefined;
        if (!id) {
          throw new Error("Missing id for update");
        }
        saved = await updateMessageTemplate(
          id,
          values as MessageTemplateUpdateRequest
        );
      }

      toast({
        title: isCreate ? "Created successfully" : "Updated successfully",
      });
      onSuccess?.(saved?.data ?? saved);
    } catch (error: any) {
      toast({
        title: "Operation failed",
        description: error?.message ?? undefined,
      });
    }
  };

  // Lightweight section wrapper for consistent spacing and headings
  const FormSection = React.useCallback(
    ({
      title,
      description,
      children,
    }: {
      title: string;
      description?: string;
      children: React.ReactNode;
    }) => (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: description ? 0.5 : 1 }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {description}
          </Typography>
        ) : null}
        {children}
      </Box>
    ),
    []
  );

  const submitHandler = useCallback(
    () => handleSubmit(onSubmit)(),
    [handleSubmit, onSubmit]
  );

  useExposeFormHandlers({
    onFormReady,
    createSubmit: submitHandler,
    isSubmitting,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <FormSection
                title="Content"
                description="Compose the message body. You can insert placeholders like {{customerName}}."
              >
                <Controller
                  name={"content" as any}
                  control={control}
                  render={({ field }) => (
                    <MyRichTextEditor
                      value={(field.value as string) ?? ""}
                      onChange={field.onChange}
                      error={!!errors.content}
                    />
                  )}
                />
                {errors.content?.message && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {errors.content.message as string}
                  </FormHelperText>
                )}
              </FormSection>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <FormSection title="Basic information">
                <Grid container gap={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Name"
                      placeholder="e.g. Welcome new customer"
                      InputLabelProps={{ shrink: true }}
                      {...register("name" as any)}
                      error={!!errors.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Key"
                      placeholder="e.g. welcome_new_user"
                      InputLabelProps={{ shrink: true }}
                      {...register("key" as any)}
                      error={!!errors.key}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!errors.category}>
                      <InputLabel id="category-label">Category</InputLabel>
                      <Controller
                        name={"category" as any}
                        control={control}
                        render={({ field }) => (
                          <Select
                            labelId="category-label"
                            label="Category"
                            value={(field.value as string) ?? "General"}
                            onChange={field.onChange}
                          >
                            {categoryOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.category?.message && (
                        <FormHelperText>
                          {errors.category.message as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </FormSection>

              <Divider sx={{ my: 2 }} />

              <FormSection title="Status">
                <FormControlLabel
                  control={
                    <Switch
                      defaultChecked={initialValues?.isActive ?? true}
                      {...register("active" as any)}
                    />
                  }
                  label="Active"
                />
              </FormSection>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </form>
  );
}

export default MessageTemplateForm;
